import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  BookmarkSimpleIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  CheckIcon,
  PencilIcon,
  PresentationChartIcon,
  TextIndentIcon,
  WarningIcon,
  EyeIcon,
  GearSixIcon,
} from '@phosphor-icons/react';
import moment from 'moment';
import { toast } from 'react-hot-toast';
import { Switch } from '../components/ui/switch';
import SolutionsLoader from '../components/loaders/SolutionsLoader';
import axios from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DarkModeToggle } from '../Layout/DarkModeToggle';
import Latex from '../../../../apps/frontend/src/components/global/Latex';
import { SmallTab, SmallTabsContainer, Tab, TabsContainer } from '../utils/SubjectTabs';
import { Button } from '../components/ui/button';
import QuestionTags from './components/QuestionTags';
import QuestionAnalytics from './components/QuestionAnalytics';
import SingleQuestionJourney from './components/SingleQuestionJourney';
import SettingsDropdown from './components/SettingsDropdown';
import SolutionTabs from './components/SolutionTabs';
import CommunitySolutionBanner from './components/CommunitySolutionBanner';
import CommunitySolutionPanel from './components/CommunitySolutionPanel';
import { getNumericalOption, getOption } from '../components/solution/Options';
import getQuestionText from '../components/solution/Question';
import { ArrowUpIcon } from 'lucide-react';
import hotkeys from 'hotkeys-js';
import SimilarQuestionSparkle from '../../../../apps/frontend/public/icons/similar-question-sparkle.svg';
import { generateWatermarks } from '../../../../apps/frontend/src/utils/waterMark';
import { ThemeContext } from '../../../../apps/frontend/src/context/ThemeStore';
import QuestionListing from './components/QuestionListing';
import ReportModal from '../components/modals/ReportModal';
import BookmarkDrawerGlobal from '../components/drawers/BookmarkDrawer';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ContextMenu } from '../components/contextmenu/ContextMenu';
import CustomTooltip from '../components/tooltip/CustomTooltip';
import VideoCipherPlayer from '../components/solution/VideoCipherPlayer';
import AttemptSimilarQsModal from '../SimilarQuestion/AttemptSimilarQsModal';
import TorchGuide from '../components/generic/TorchGuide';
import Fancybox from '../lib/FancyBox';
import LearnMore from '../components/buttons/LearnMore';

const QUESTION_REPORT_LIMIT = 10;
const DAY_LIMIT = 7;
const OPTIONS = [
  {
    checked: false,
    text: 'Error in question/option(s)/solution',
  },
  {
    checked: false,
    text: 'Missing/Wrong/Bad quality image(s)',
  },
  {
    checked: false,
    text: 'Wrong Answer Key',
  },
  {
    checked: false,
    text: 'Incorrect Solution',
  },
  {
    checked: false,
    text: 'Repeated Question',
  },
];

export default function Solutions({
  qNo = 1,
  isDrawer = false,
  packId: propPackId, // Optional prop
  testId: propTestId, // Optional prop
  showReportButton = true,
  showSolution = true,
  onShowSolution,
}) {
  // const user = JSON.parse(localStorage.getItem('userProfile'));
  const isLms = sessionStorage.getItem('lms');
  const { isDarkTheme } = useContext(ThemeContext);
  const params = useParams();
  // Use props if available, otherwise fall back to URL params
  const packId = propPackId || params.packId;
  const testId = propTestId || params.testId;
  const navigate = useNavigate();
  const location = useLocation();
  const targetQuestionIdRef = useRef(new URLSearchParams(location.search).get('questionId'));

  const contextMenuItems = [
    {
      id: 'bookmark',
      label: 'Bookmark (Add to Notebooks)',
      icon: BookmarkSimpleIcon,
      action: 'bookmark',
      hoverBg: 'q3-surface-dimmer',
    },
    {
      id: 'questionPalette',
      label: 'Toggle Question Palette',
      icon: TextIndentIcon,
      action: 'toggleQuestionPalette',
      hoverBg: 'q3-surface-dimmer',
    },
    {
      id: 'attemptAnalysis',
      label: 'Toggle Attempt Analysis',
      icon: PresentationChartIcon,
      action: 'toggleAttemptAnalysis',
      hoverBg: 'q3-surface-dimmer',
    },
  ];

  const handleContextMenuAction = (action) => {
    switch (action) {
      case 'bookmark':
        setShowBookmarkDrawer(true);
        break;
      case 'toggleQuestionPalette':
        setIsSidebarOpen(!isSidebarOpen);
        break;
      case 'toggleAttemptAnalysis':
        setShowAttemptAnalysis(!showAttemptAnalysis);
        break;
      default:
        break;
    }
  };

  const journeyScrollRef = useRef(null);
  const questionFocusRef = useRef(null);
  const questionMetaDataFocusRef = useRef(null);
  const solutionRef = useRef(null);
  const skipSubjectEffectRef = useRef(false);
  const subjectChangeInProgressRef = useRef(false);
  const commentsRef = useRef('');
  const similarQuestionButtonRef = useRef(null);

  const scrollJourney = (direction) => {
    if (!journeyScrollRef.current) return;
    journeyScrollRef.current.scrollBy({
      left: direction === 'left' ? -300 : 300,
      behavior: 'smooth',
    });
  };

  const steps = [
    {
      target: '.similar-question-button-target',
      content: 'Click here to Practice Similar Questions',
      title: 'Attempt Similar Questions',
      disableBeacon: true,
      spotlightPadding: 0,
    },
  ];

  const scrollQuestion = () => {
    if (!isQuestionInFocus) {
      questionMetaDataFocusRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else {
      solutionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // report states
  const [reportMessage, setReportMessage] = useState({
    success: true,
    message: 'Can Report',
    daysLeft: DAY_LIMIT,
    count: QUESTION_REPORT_LIMIT,
  });
  const [reportStat, setReportStat] = useState({
    lastReported: null,
    currentReportCount: 0,
  });
  const [isReported, setIsReported] = useState(false);
  const [solutionTab, setSolutionTab] = useState('quizrr');
  // Keeps CommunitySolutionPanel mounted (CSS-hidden, not unmounted) once the
  // user has viewed it for the current question, so toggling back to it
  // doesn't re-trigger its data fetch. Reset per-question in the effect below.
  const [communityTabVisited, setCommunityTabVisited] = useState(false);
  const handleSolutionTabChange = (tab) => {
    setSolutionTab(tab);
    if (tab === 'community') setCommunityTabVisited(true);
  };
  const [myCommunitySolution, setMyCommunitySolution] = useState(null);
  const [communitySolutionsTotal, setCommunitySolutionsTotal] = useState(0);
  const [hasCommunityWriteAccess, setHasCommunityWriteAccess] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSolutionLoading, setIsSolutionLoading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportOptions, setReportOptions] = useState(OPTIONS);

  const [contextMenu, setContextMenu] = useState(false);

  // bookmark states
  const [showBookmarkDrawer, setShowBookmarkDrawer] = useState(false);

  // initial load states
  const [testDetails, setTestDetails] = useState(null);
  const [subjectMeta, setSubjectMeta] = useState(null);
  const [packDetails, setPackDetails] = useState(null);

  // solution states
  const [solutions, setSolutions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [currentSolution, setCurrentSolution] = useState(null);
  const [questionId, setQuestionId] = useState(null);
  const [questionNotebooks, setQuestionNotebooks] = useState(null);
  const [questionNotes, setQuestionNotes] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [journeyData, setJourneyData] = useState(null);
  const [showAttemptAnalysis, setShowAttemptAnalysis] = useState(true);

  // Settings states
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [questionSolutionMode, setQuestionSolutionMode] = useState(false);
  const [tempQuestionSolutionMode, setTempQuestionSolutionMode] = useState(false);
  const [tempShowAttemptAnalysis, setTempShowAttemptAnalysis] = useState(true);

  // Local solution visibility state
  const [localShowSolution, setLocalShowSolution] = useState(questionSolutionMode || isDrawer);

  // attempt similar question status
  const [showSimilarQsModal, setShowSimilarQsModal] = useState(false);
  const [similarQsLoading, setSimilarQsLoading] = useState(false);
  const [similarQsData, setSimilarQsData] = useState(null);
  const [showSimilarButton, setShowSimilarButton] = useState(false);
  const [similarQuestionsViewed, setSimilarQuestionsViewed] = useState(false);
  const [similarButtonCache, setSimilarButtonCache] = useState({});

  // Maintain cache for similar questions across questionIds
  const [similarQsCache, setSimilarQsCache] = useState({}); // { [questionId]: data }

  // pagination states
  const [offset, setOffset] = useState(qNo - 1);

  // focus states
  const [isQuestionInFocus, setIsQuestionInFocus] = useState(true);

  // question tag states
  const [questionTags, setQuestionTags] = useState(null);
  const [userQuestionTag, setUserQuestionTag] = useState(null);

  // subject tabs
  const [selectedSubject, setSelectedSubject] = useState(null);

  // sidebar states
  const [isSidebarOpen, setIsSidebarOpen] = useState(isDrawer);
  const [questionStatus, setQuestionStatus] = useState([]);
  const [questionData, setQuestionData] = useState(null);
  const [selectedSidebarTab, setSelectedSidebarTab] = useState({
    color: 'var(--q3-accent-light)',
    textColor: 'var(--q3-accent-normal)',
    count: 0,
    title: 'All',
  });

  const [user, setUser] = useState(JSON.parse(localStorage.getItem('userProfile')));
  const [showTorchGuide, setShowTorchGuide] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [showQuestionSolutionModeTour, setShowQuestionSolutionModeTour] = useState(true);
  const [questionSolutionModeStepIndex, setQuestionSolutionModeStepIndex] = useState(0);

  const questionSolutionModeSteps = [
    {
      target: '.settings-button-target',
      content: 'Click here to access solution settings',
      title: 'Solution Settings',
      disableBeacon: true,
      spotlightPadding: 0,
    },
    {
      target: '.question-solution-mode-target',
      content: 'Enable Question Solution Mode to see solutions automatically when you reopen questions',
      title: 'Question Solution Mode',
      disableBeacon: true,
      spotlightPadding: 0,
    },
  ];

  // Fetch user settings for question solution mode only
  const fetchUserSettings = async () => {
    // If API fails, use localStorage as fallback
    const user = JSON.parse(localStorage.getItem('userProfile'));
    const localSettings = user.settings;
    setQuestionSolutionMode(localSettings.questionSolutionMode || true);
    setTempQuestionSolutionMode(localSettings.questionSolutionMode || true);
    setLocalShowSolution(localSettings.questionSolutionMode || true);
  };

  // Save user settings for question solution mode only
  const saveUserSettings = async (settings) => {
    try {
      await axios.patch('/api/user/profile/settings', settings);
      // Also save locally as backup
      localStorage.setItem('solutionSettings', JSON.stringify(settings));
    } catch (error) {
      // Fallback to localStorage if API fails
      localStorage.setItem('solutionSettings', JSON.stringify(settings));
    }
  };

  const handleApplySettings = async () => {
    // Check what actually changed
    const questionSolutionModeChanged = tempQuestionSolutionMode !== questionSolutionMode;
    const onlyShowAttemptAnalysisChanged =
      tempShowAttemptAnalysis !== showAttemptAnalysis && !questionSolutionModeChanged;

    setQuestionSolutionMode(tempQuestionSolutionMode);
    setShowAttemptAnalysis(tempShowAttemptAnalysis);
    setLocalShowSolution(tempQuestionSolutionMode);

    if (questionSolutionModeChanged) {
      const newSettings = {
        questionSolutionMode: tempQuestionSolutionMode,
      };
      await saveUserSettings(newSettings);
      toast.success('Settings applied successfully');
    } else if (onlyShowAttemptAnalysisChanged) {
      toast.success('Settings applied successfully');
    } else {
      toast.success('Settings applied successfully');
    }

    setShowSettingsDropdown(false);
  };

  const handleClearAll = () => {
    setTempQuestionSolutionMode(false);
    setTempShowAttemptAnalysis(false);
  };

  // Mark question solution mode tour as done
  const markQuestionSolutionModeTourDone = async () => {
    try {
      await axios.patch('/api/user/profile', {
        questionSolutionModeTourDone: true,
      });
      const updatedUser = {
        ...user,
        questionSolutionModeTourDone: true,
      };
      localStorage.setItem('userProfile', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to mark tour as done:', error);
    }
  };

  async function fetchTestDetails() {
    try {
      const response = await axios.get(`/api/test/solution/testDetails/${testId}?packId=${packId}`);
      if (response.status === 200) {
        setTestDetails(response?.data?.data);
        setPackDetails(response?.data?.pack);
        const subjMeta = response?.data?.subjectMeta;
        const subjects = response?.data?.data?.subjects;
        const subjectMetaArray = [];
        let i = 0;
        for (const subject of subjects) {
          const title = subject.subjectId.title;
          subjMeta[title].totalQuestions = subject.totalQuestions;
          subjMeta[title].index = i;
          subjectMetaArray.push(subjMeta[title]);
          i++;
        }
        setSubjectMeta(subjectMetaArray);
        setSelectedSubject(subjectMetaArray[0]);
      }
    } catch (error) {}
  }

  async function fetchSolution() {
    try {
      setIsSolutionLoading(true);
      const response = await axios.get(`/api/test/solution/${testId}?pack=${packId}&offset=0&limit=300`, {
        headers: {
          'x-qzr-no-compression': true,
        },
      });
      if (response.status === 200) {
        const fetchedSolutions = response?.data?.data;
        setSolutions(fetchedSolutions);

        let resolvedOffset = offset || 0;
        const targetQuestionId = targetQuestionIdRef.current;
        if (targetQuestionId) {
          const matchedIndex = fetchedSolutions?.findIndex(
            (sol) => sol?.questionId?._id?.toString() === targetQuestionId,
          );
          if (matchedIndex != null && matchedIndex >= 0) {
            resolvedOffset = matchedIndex;
            setOffset(matchedIndex);
          }
          targetQuestionIdRef.current = null;
        }

        const currentSolution = fetchedSolutions[resolvedOffset];
        window.webengage.track('Viewed Solution', {
          'User Id': user?.email,
          'Pack Id': packId,
          'Pack Name': currentSolution?.questionId?.packTitle,
          'Test Id': testId,
          'Test Name': testDetails?.title,
          'Exam Name': currentSolution?.questionId?.examTitle,
          'Subject Name': currentSolution?.questionId?.subjectTitle,
          'Chapter Name': currentSolution?.questionId?.chapters[0]?.title,
          'Topic Name': currentSolution?.questionId?.topics[0]?.title,
          'Concept Name': currentSolution?.questionId?.concepts[0]?.title,
          'Question Type': currentSolution?.questionId?.type,
          'Question Level': currentSolution?.questionId?.level,
          'Attempt Status': currentSolution?.status,
        });
        setCurrentSolution(currentSolution);
        setTotalQuestions(response?.data?.count);
        setQuestionId(currentSolution?.questionId._id);
      }
      setIsSolutionLoading(false);
    } catch (error) {
      navigate(`/${packId}/${testId}/analysis`);
    }
  }

  // Check if similar questions button should be shown
  const checkSimilarQuestionsButton = async (questionId) => {
    if (!questionId) {
      return;
    }

    // Check localStorage cache first
    const cachedButtons = JSON.parse(localStorage.getItem('similarButtonCache') || '{}');
    if (cachedButtons[questionId]) {
      setShowSimilarButton(true);
      return;
    }

    try {
      const response = await axios.get(`/api/v1/similar-question/${questionId}/show-button`);
      if (response.status === 200) {
        const shouldShow = response.data.showSimilarQuestionsButton;
        setShowSimilarButton(shouldShow);

        // Cache in localStorage if should show
        if (shouldShow) {
          const updatedCache = { ...cachedButtons, [questionId]: true };
          localStorage.setItem('similarButtonCache', JSON.stringify(updatedCache));
        }
      }
    } catch (error) {
      setShowSimilarButton(false);
    }
  };

  // Fetch community solution summary (own submission + total approved from others)
  const fetchCommunitySolutionSummary = async (questionId) => {
    if (!questionId) return;

    try {
      const mySolutionRes = await axios.get(`/api/v1/community-solution/${questionId}/my-solution`);
      setMyCommunitySolution(mySolutionRes?.data?.data ?? null);
      setHasCommunityWriteAccess(!mySolutionRes?.data?.data?.hasWriteAccess?.uploadBlocked);
    } catch (error) {
      setMyCommunitySolution(null);
      setHasCommunityWriteAccess(true);
    }

    try {
      const listRes = await axios.get(`/api/v1/community-solution/${questionId}?limit=1&offset=0`);
      setCommunitySolutionsTotal(listRes?.data?.data?.total ?? 0);
    } catch (error) {
      setCommunitySolutionsTotal(0);
    }
  };

  // Track that user has viewed similar questions
  const trackSimilarQuestionsViewed = async () => {
    try {
      const res = await axios.post(`/api/v1/similar-question/${questionId}/track`);
      setSimilarQuestionsViewed(true);
    } catch (error) {}
  };

  // Get similar questions
  const getSimilarQuestions = async () => {
    try {
      const response = await axios.get(`/api/v1/similar-question/${questionId}/similar-questions`);

      if (response.status === 200) {
        const data = {
          questions: response.data.similarQuestions,
          packId,
          testId,
        };

        // Save to both current state and cache
        setSimilarQsData(data);
        setSimilarQsCache((prev) => ({ ...prev, [questionId]: data }));
      }
    } catch (error) {
      toast.error('Failed to fetch similar questions');
    }
  };

  const handleAttemptSimilarQs = async () => {
    window.webengage.track('btn_click', {
      name: 'Attempt Similar Questions',
      source: 'solutions',
    });
    setShowSimilarQsModal(true);

    // Check cache first — no loader if data is already cached
    if (similarQsCache[questionId]) {
      setSimilarQsData(similarQsCache[questionId]);
      return;
    }

    // Fetch only if not cached
    if (!similarQsData) {
      setSimilarQsLoading(true);
      await getSimilarQuestions();
      setSimilarQsLoading(false);
    }

    if (!similarQuestionsViewed) {
      trackSimilarQuestionsViewed().catch(() => {});
    }
  };

  const updateSolution = () => {
    const currentSolution = solutions[offset];
    window.webengage.track('Viewed Solution', {
      'User Id': user?.email,
      'Pack Id': packId,
      'Pack Name': currentSolution?.questionId?.packTitle,
      'Test Id': testId,
      'Test Name': testDetails?.title,
      'Exam Name': currentSolution?.questionId?.examName,
      'Subject Name': currentSolution?.questionId?.subjectTitle,
      'Chapter Name': currentSolution?.questionId?.chapters[0]?.title,
      'Topic Name': currentSolution?.questionId?.topics[0]?.title,
      'Concept Name': currentSolution?.questionId?.concepts[0]?.title,
      'Question Type': currentSolution?.questionId?.type,
      'Question Level': currentSolution?.questionId?.level,
      'Attempt Status': currentSolution?.status,
      'Evaluation Status': getEvaluationStatusForEvent(currentSolution),
    });
    setCurrentSolution(currentSolution);
    setQuestionId(currentSolution?.questionId._id);
  };

  async function getAvailableQuestionTags(isUnattempted) {
    try {
      const response = await axios.get(`/api/question-tag${isUnattempted ? '?isUnattempted=true' : ''}`);
      if (response.status === 200) {
        setQuestionTags(response?.data?.data);
      }
    } catch (error) {}
  }

  async function getQuestionNotebooks() {
    try {
      const response = await axios.get(`/api/v1/notebooks/question-status/${questionId}`);
      if (response.status === 200) {
        setQuestionNotebooks(response?.data?.data?.notebooks);
      }
    } catch (error) {}
  }

  async function getQuestionNotes() {
    try {
      const response = await axios.get(`/api/v2/notes/${questionId}`);
      if (response.status === 200) {
        setQuestionNotes(response?.data?.data);
      }
    } catch (error) {}
  }

  async function getQuestionTags() {
    try {
      const response = await axios.get(`/api/question-tag/get-tags/${packId}/${testId}?questionId=${questionId}`);
      if (response.status === 200) {
        setUserQuestionTag(response?.data?.data?.tags[0]);
      }
    } catch (error) {}
  }

  async function getQuestionComparison() {
    try {
      const response = await axios.get(`/api/test/solution/${packId}/${testId}/compare/${questionId}`);
      if (response.status === 200) {
        setComparisonData(response?.data?.data);
      }
    } catch (error) {}
  }

  async function getQuestionJourney() {
    const response = await axios.get(
      `/api/test/analysis/v2/${packId}/${testId}/singleQuestionJourney?question=${currentSolution?.questionId?._id}`,
    );
    if (response?.data && response?.data?.data) {
      setJourneyData(response?.data?.data);
    } else {
      setJourneyData(null);
    }
  }

  async function getQuestionStatuses() {
    const response = await axios.get(`api/test/${testId}/questions/status`);
    if (response?.data && response?.data?.data) {
      setQuestionStatus(response?.data?.data);
    }
  }

  const handleSubjectChange = (subjectData) => {
    subjectChangeInProgressRef.current = true;
    setSelectedSubject(subjectData);
  };

  const handleBookmarkDrawer = () => {
    setShowBookmarkDrawer(!showBookmarkDrawer);
  };

  const handleSidebarTabSwitch = (sidebarTabData) => {
    setSelectedSidebarTab(sidebarTabData);
  };

  const handleQuestionNotes = (updatedQuestionNotes) => {
    setQuestionNotes(updatedQuestionNotes);
  };

  const handleQuestionBoomark = (updatedQuestionNotebooks) => {
    setQuestionNotebooks(updatedQuestionNotebooks);
  };

  const handleQuestionChange = (isPrev = false, offsetCount = -1) => {
    if (offsetCount === -1) {
      if (selectedSidebarTab?.title?.toLowerCase() !== 'all') {
        const filteredList = questionStatus.filter((question) => {
          if (question.subjectId.toString() === selectedSubject?.id.toString()) {
            if (selectedSidebarTab.title === 'Correct') {
              return question.isCorrect;
            } else if (selectedSidebarTab.title === 'Incorrect') {
              return !question.isCorrect && (question.status === 'answered' || question.status === 'answeredReview');
            } else if (selectedSidebarTab.title === 'Unattempted') {
              return (
                question.status === 'notVisited' ||
                question.status === 'notAnswered' ||
                question.status === 'markedReview'
              );
            }
            return true;
          }
          return false;
        });

        const currentIndexInFiltered = filteredList.findIndex((item) => item.qNo === offset + 1);
        let nextIndex = isPrev ? currentIndexInFiltered - 1 : currentIndexInFiltered + 1;
        if (nextIndex >= 0 && nextIndex < filteredList.length) {
          const nextQNo = filteredList[nextIndex].qNo;
          setOffset(nextQNo - 1); // setOffset is 0-indexed
        }
      } else {
        skipSubjectEffectRef.current = true;
        if (isPrev && offset > 0) {
          setOffset(offset - 1);
        }
        if (!isPrev && offset < totalQuestions - 1) {
          setOffset(offset + 1);
        }
      }
    } else {
      setOffset(offsetCount);
    }

    if (window.innerWidth <= 1024 || isDrawer) {
      setIsSidebarOpen(false);
    }
  };

  const prepareQuestionStatusData = () => {
    if (questionStatus && questionStatus?.length > 0) {
      const questionStatusMap = {
        All: {
          color: 'var(--q3-accent-light)',
          textColor: 'var(--q3-accent-normal)',
          title: 'All',
        },
        Correct: {
          color: 'var(--q3-success-light)',
          textColor: 'var(--q3-success-normal)',
          title: 'Correct',
        },
        Incorrect: {
          color: 'var(--q3-danger-light)',
          textColor: 'var(--q3-danger-normal)',
          title: 'Incorrect',
        },
        Unattempted: {
          color: 'var(--q3-surface-dimmer)',
          textColor: 'var(--q3-neutral-default)',
          title: 'Unattempted',
        },
      };

      questionStatus?.forEach((status) => {
        const subjectId = status?.subjectId?.toString();
        // if it doesn't exist, set 1 otherwise add 1
        if (!questionStatusMap['All'][subjectId]) {
          questionStatusMap['All'][subjectId] = { count: 0 };
        }
        questionStatusMap['All'][subjectId].count += 1;
        if (status.isCorrect) {
          if (!questionStatusMap['Correct'][subjectId]) {
            questionStatusMap['Correct'][subjectId] = { count: 0 };
          }
          questionStatusMap['Correct'][subjectId].count += 1;
        }
        if ((status.status === 'answered' || status.status === 'answeredReview') && !status.isCorrect) {
          if (!questionStatusMap['Incorrect'][subjectId]) {
            questionStatusMap['Incorrect'][subjectId] = { count: 0 };
          }
          questionStatusMap['Incorrect'][subjectId].count += 1;
        }
        if (status.status === 'notAnswered' || status.status === 'notVisited' || status.status === 'markedReview') {
          if (!questionStatusMap['Unattempted'][subjectId]) {
            questionStatusMap['Unattempted'][subjectId] = { count: 0 };
          }
          questionStatusMap['Unattempted'][subjectId].count += 1;
        }
      });

      setQuestionData(Object.values(questionStatusMap));
    }
  };

  const isQuestionAlreadyReported = () => {
    const questions = localStorage.getItem('reportedQuestions');
    if (questions && questions?.includes(currentSolution?.questionId?._id)) {
      setIsReported(true);
    } else {
      setIsReported(false);
    }
  };

  const checkQuestionReportLimit = () => {
    const rstat = JSON.parse(localStorage.getItem('reportStat'));
    if (!rstat) return 1;
    const current = moment(new Date());
    const start = moment(rstat.lastReported);

    const daysLeft = current.diff(start, 'days');
    if (daysLeft >= DAY_LIMIT) {
      localStorage.removeItem('reportStat');
      setReportMessage({
        success: true,
        message: '',
        daysLeft: DAY_LIMIT,
        count: QUESTION_REPORT_LIMIT,
      });
      return 1;
    }

    setReportMessage({
      ...reportMessage,
      daysLeft: DAY_LIMIT - daysLeft,
      count: QUESTION_REPORT_LIMIT - rstat.currentReportCount,
    });

    if (rstat.currentReportCount >= QUESTION_REPORT_LIMIT) {
      setReportMessage({
        success: false,
        message: `You have reached report limit. Please wait ${DAY_LIMIT - daysLeft} days to report more.`,
        daysLeft: DAY_LIMIT - daysLeft,
        count: QUESTION_REPORT_LIMIT - rstat.currentReportCount,
      });
      return 0;
    }

    return 1;
  };

  const reportQuestion = () => {
    setIsLoading(true);
    const limitCheck = checkQuestionReportLimit();
    if (!limitCheck) {
      return null;
    }
    axios
      .post(`/api/content/question/report/${currentSolution?.questionId?._id}`, {
        errorTypes: reportOptions.map((opt) => {
          if (opt.checked) return opt.text;
          return null;
        }),
        comments: commentsRef.current.value,
        studentQuestionLink:
          window.location.protocol + '//' + window.location.host + window.location.pathname + `?question=${offset + 1}`,
      })
      .then((response) => {
        setIsReported(true);
        let rQs = JSON.parse(localStorage.getItem('reportedQuestions'));
        if (rQs) {
          rQs.push(currentSolution?.questionId?._id);
          localStorage.setItem('reportedQuestions', JSON.stringify(rQs));
        } else {
          localStorage.setItem('reportedQuestions', JSON.stringify([currentSolution?.questionId?._id]));
        }

        const rstat = JSON.parse(localStorage.getItem('reportStat'));
        if (rstat) {
          localStorage.setItem(
            'reportStat',
            JSON.stringify({
              ...rstat,
              currentReportCount: rstat.currentReportCount + 1,
            }),
          );
        } else {
          localStorage.setItem(
            'reportStat',
            JSON.stringify({
              lastReported: new Date(),
              currentReportCount: 1,
            }),
          );
        }
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        checkQuestionReportLimit();
      });
  };

  const handleOnChange = (value, clickIndex) => {
    let newSet = [];
    for (let index = 0; index < reportOptions.length; index++) {
      let element = reportOptions[index];
      if (index === clickIndex) {
        element.checked = !element.checked;
      }
      newSet.push(element);
    }
    setReportOptions(newSet);
  };

  useEffect(() => {
    fetchUserSettings();
    fetchTestDetails();
    getQuestionStatuses();
    fetchSolution();
  }, []);

  useEffect(() => {
    const checkIfTablet = () => {
      setIsSidebarOpen(window.innerWidth > 1024);
    };

    checkIfTablet();
    window.addEventListener('resize', checkIfTablet);
    return () => window.removeEventListener('resize', checkIfTablet);
  }, []);

  //torch guide use-effect
  useEffect(() => {
    const checkIfTablet = () => {
      setShowTorchGuide(window.innerWidth > 1024);
    };

    checkIfTablet();
    window.addEventListener('resize', checkIfTablet);

    // Check if question solution mode tour should be shown
    if (user && !user.questionSolutionModeTourDone) {
      setShowQuestionSolutionModeTour(true);
    }

    return () => window.removeEventListener('resize', checkIfTablet);
  }, [user]);

  useEffect(() => {
    // if user has questionSolutionModeTourDone as false, show settings by default
    if (user?.questionSolutionModeTourDone) return;
    setShowSettingsDropdown(true);
  }, [user]);

  // Add torch guide callback handler
  const handleJoyrideCallback = async (data) => {
    const { status, index, type, action } = data;

    if (status === 'finished') {
      setStepIndex(0);
      await axios.patch('/api/user/profile', {
        similarQuestionTourDone: true,
      });
      const updatedUser = {
        ...user,
        similarQuestionTourDone: true,
      };
      localStorage.setItem('userProfile', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } else if (type === 'step:after') {
      if (action === 'next') {
        setStepIndex(index + 1);
      } else if (action === 'prev') {
        setStepIndex(Math.max(0, index - 1));
      }
    }
  };

  const handleQuestionSolutionModeTourCallback = async (data) => {
    const { status, index, type, action } = data;

    if (status === 'finished') {
      setQuestionSolutionModeStepIndex(0);
      setShowQuestionSolutionModeTour(false);
      await markQuestionSolutionModeTourDone();
    } else if (type === 'step:after') {
      if (action === 'next') {
        setQuestionSolutionModeStepIndex(index + 1);
      } else if (action === 'prev') {
        setQuestionSolutionModeStepIndex(Math.max(0, index - 1));
      }
    }
  };

  useEffect(() => {
    let scrollEl;
    let observer;

    const waitUntilReady = setInterval(() => {
      const container = document.getElementById('scrollable-container');

      // ensure both exist
      if (container && questionFocusRef.current) {
        clearInterval(waitUntilReady);

        scrollEl = container;

        observer = new IntersectionObserver(
          ([entry]) => {
            setIsQuestionInFocus(entry.intersectionRatio > 0.3);
          },
          {
            root: scrollEl,
            threshold: [0, 0.3, 0.6, 1],
          },
        );

        observer.observe(questionFocusRef.current);
      }
    }, 50);

    return () => {
      clearInterval(waitUntilReady);
      if (observer && questionFocusRef.current) {
        observer.unobserve(questionFocusRef.current);
      }
    };
  }, []);

  useEffect(() => {
    prepareQuestionStatusData();
  }, [questionStatus]);

  useEffect(() => {
    if (!selectedSidebarTab) return;

    // Don't auto-navigate if in drawer mode
    if (isDrawer) return;

    const filteredList = questionStatus?.filter((question) => {
      if (question.subjectId.toString() === selectedSubject?.id.toString()) {
        if (selectedSidebarTab.title === 'Correct') {
          return question.isCorrect;
        } else if (selectedSidebarTab.title === 'Incorrect') {
          return !question.isCorrect && (question.status === 'answered' || question.status === 'answeredReview');
        } else if (selectedSidebarTab.title === 'Unattempted') {
          return (
            question.status === 'notVisited' || question.status === 'notAnswered' || question.status === 'markedReview'
          );
        }
        return true;
      }
      return false;
    });
    if (!filteredList || filteredList.length === 0) return;
    handleQuestionChange(false, filteredList[0]?.qNo - 1);
  }, [selectedSidebarTab]);

  useEffect(() => {
    if (!user || !currentSolution) return;

    // Add a small delay to ensure DOM elements are rendered
    const timer = setTimeout(() => {
      const questionElement = document.getElementById('question-section');
      const solutionElement = document.getElementById('solution-section');

      // Only generate watermarks if elements exist
      if (questionElement) {
        generateWatermarks({
          email: user?.email,
          mobileNumber: user?.mobileNumber,
          elementId: 'question-section',
          opacity: isDarkTheme ? 0.3 : 0.5,
        });
      }

      if (solutionElement) {
        generateWatermarks({
          email: user?.email,
          mobileNumber: user?.mobileNumber,
          elementId: 'solution-section',
          opacity: isDarkTheme ? 0.3 : 0.5,
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [user, currentSolution, isDarkTheme, localShowSolution, showSolution, questionSolutionMode, isSidebarOpen]);

  useEffect(() => {
    const nextShortcut = 'right';
    const prevShortcut = 'left';
    const hideShortcut = 'shift+h';
    const bookmarkShortcut = 'shift+b';
    const handler = (event, hotkey) => {
      event.preventDefault();
      if (hotkey.key === nextShortcut) {
        if (offset < totalQuestions - 1) handleQuestionChange(false);
      } else if (hotkey.key === prevShortcut) {
        if (offset > 0) handleQuestionChange(true);
      } else if (hotkey.key === hideShortcut) {
        setIsSidebarOpen((prev) => !prev);
      } else if (hotkey.key === bookmarkShortcut) {
        handleBookmarkDrawer();
      }
    };
    hotkeys(nextShortcut, handler);
    hotkeys(prevShortcut, handler);
    hotkeys(hideShortcut, handler);
    hotkeys(bookmarkShortcut, handler);

    return () => {
      hotkeys.unbind(nextShortcut, handler);
      hotkeys.unbind(prevShortcut, handler);
      hotkeys.unbind(hideShortcut, handler);
      hotkeys.unbind(bookmarkShortcut, handler);
    };
  }, [offset, totalQuestions]);

  useEffect(() => {
    setOffset(qNo - 1);
  }, [qNo]);

  useEffect(() => {
    updateSolution();
  }, [offset]);

  // On navigating to a different question, reset the page scroll to the top so
  // the user starts at the question instead of wherever they'd scrolled to
  // (e.g. deep in the community-solutions section) on the previous question.
  useEffect(() => {
    if (!questionId) return;
    document.getElementById('scrollable-container')?.scrollTo({ top: 0 });
  }, [questionId]);

  useEffect(() => {
    if (!questionId) return;

    // Reset flags, but don't clear cache
    setSimilarQuestionsViewed(false);
    setShowSimilarButton(false);
    // Reset localShowSolution based on questionSolutionMode when question changes
    setLocalShowSolution(questionSolutionMode);

    getQuestionNotebooks();
    getQuestionComparison();
    getAvailableQuestionTags(
      getEvaluationStatusForEvent() === 'Marked for Review' || getEvaluationStatusForEvent() === 'Not Answered',
    );
    getQuestionTags();
    getQuestionNotes();
    getQuestionJourney();
    // Don't overwrite selectedSubject when user just clicked a subject tab (avoids resetting to first subject)
    if (!subjectChangeInProgressRef.current) {
      // Syncing the subject tab to match the current question shouldn't re-trigger
      // the subject-change effect's auto-navigation to that subject's first question
      // (would override direct navigation, e.g. from a questionId query param).
      skipSubjectEffectRef.current = true;
      setSelectedSubject(
        subjectMeta?.find((item) => item.id.toString() === currentSolution?.questionId?.subjects[0]?.toString()),
      );
    } else {
      subjectChangeInProgressRef.current = false;
    }
    isQuestionAlreadyReported();
    checkQuestionReportLimit();
    if (currentSolution?.marksScored <= 0) {
      checkSimilarQuestionsButton(questionId);
    }
    fetchCommunitySolutionSummary(questionId);
    setCommunityTabVisited(false);
    setSolutionTab('quizrr');

    // If cached, reuse instantly
    if (similarQsCache[questionId]) {
      setSimilarQsData(similarQsCache[questionId]);
    } else {
      setSimilarQsData(null);
    }
  }, [questionId, currentSolution, questionSolutionMode]);

  useEffect(() => {
    if (!selectedSubject) return;

    if (skipSubjectEffectRef.current) {
      skipSubjectEffectRef.current = false;
      return;
    }

    // Don't auto-navigate if in drawer mode - let the drawer control navigation
    if (isDrawer) return;

    // A questionId query param navigation hasn't been resolved yet - don't let the
    // subject tab (still defaulting to the first subject) jump us to its first question.
    if (targetQuestionIdRef.current) return;

    const subjectIdStr = selectedSubject?.id?.toString();
    if (!subjectIdStr) return;

    // Pick the first *occurring* question for this subject in the actual test order
    // (solutions[] is already ordered by the test question sequence, including any reordering).
    const firstIndexInSolutions = solutions?.findIndex((sol) => {
      const subjectIds = sol?.questionId?.subjects || [];
      return subjectIds.some((sid) => sid?.toString() === subjectIdStr);
    });

    if (firstIndexInSolutions != null && firstIndexInSolutions >= 0) {
      if (firstIndexInSolutions !== offset) {
        handleQuestionChange(false, firstIndexInSolutions);
      }
      return;
    }

    // Fallback: derive the first occurrence from questionStatus using qNo ordering.
    const firstQuestionStatus = questionStatus
      ?.filter((question) => question?.subjectId?.toString() === subjectIdStr)
      ?.sort((a, b) => (a?.qNo ?? 0) - (b?.qNo ?? 0))[0];

    if (!firstQuestionStatus?.qNo) return;

    const firstOffset = firstQuestionStatus.qNo - 1;
    if (firstOffset !== offset) {
      handleQuestionChange(false, firstOffset);
    }
  }, [selectedSubject, solutions, questionStatus, isDrawer]);

  const handleUserQuestionTagSelect = async (tagId) => {
    try {
      // If clicking the same tag, delete it
      if (userQuestionTag === tagId) {
        await axios.delete(`/api/question-tag/${packId}/${testId}?questionId=${questionId}`);
        setUserQuestionTag(null);
        toast.error('Tag removed successfully', {
          position: 'bottom-center',
          duration: 800,
        });
      } else {
        // If selecting a new tag, set it
        await axios.post(`/api/question-tag/set-tag/${packId}/${testId}?questionId=${questionId}`, {
          tag: tagId,
        });
        setUserQuestionTag(tagId);
        toast.success('Tag updated successfully', {
          position: 'bottom-center',
          duration: 800,
        });
      }
    } catch (error) {}
  };

  const getEvaluationStatusForEvent = () => {
    const type = currentSolution?.questionId?.type;
    const status = currentSolution?.status;

    if (status === 'notVisited') {
      return 'Not Visited';
    } else if (status === 'notAnswered') {
      return 'Not Answered';
    } else if (status === 'markedReview') {
      return 'Marked for Review';
    } else {
      switch (type) {
        case 'numerical':
          return currentSolution?.inputValue?.isCorrect ? 'Correct' : 'Incorrect';
        case 'singleCorrect':
          return currentSolution?.markedOptions?.find((opt) => opt.isCorrect) ? 'Correct' : 'Incorrect';
        case 'multipleCorrect':
          const isIncorrect = currentSolution?.markedOptions?.find((opt) => !opt.isCorrect);
          const correctOptionsCount = currentSolution?.questionId?.options?.filter((opt) => opt.isCorrect).length;
          const markedOptionsCount = currentSolution?.markedOptions?.filter((opt) => opt.isCorrect).length;

          return isIncorrect
            ? 'Incorrect'
            : correctOptionsCount === markedOptionsCount
              ? 'Correct'
              : 'Partially Correct';
        default:
          return null;
      }
    }
  };

  const renderSolutionSection = () => {
    // If it's a drawer, follow the showSolution prop ONLY for the solution content
    if (isDrawer) {
      if (showSolution) {
        return (
          <div id="solution-section" className="overflow-hidden relative">
            {currentSolution?.questionId?.videoSolution && (
              <VideoCipherPlayer
                key={currentSolution?.questionId?.videoSolution}
                videoId={currentSolution?.questionId?.videoSolution}
                questionId={currentSolution?.questionId?._id}
                testId={testId}
                testTitle={testDetails?.title}
                subjectTitle={currentSolution?.questionId?.subjectTitle}
                chapterTitle={currentSolution?.questionId?.chapters[0]?.title}
                topicTitle={currentSolution?.questionId?.topics[0]?.title}
                conceptTitle={currentSolution?.questionId?.concepts[0]?.title}
                questionType={currentSolution?.questionId?.type}
                questionLevel={currentSolution?.questionId?.level}
                attemptStatus={currentSolution?.status}
                source="Solutions"
              />
            )}
            <span className="font-medium text-[13px] text-[var(--q3-neutral-light)]">TEXT SOLUTION</span>
            <div className="mt-3 overflow-x-auto image-invert-wrapper">
              <Latex>{currentSolution?.questionId?.solution?.text}</Latex>
            </div>
          </div>
        );
      } else {
        // Show nothing when showSolution is false in drawer mode
        return null;
      }
    }

    // Original logic for non-drawer mode
    // If question solution mode is OFF and solution is not shown, show nothing
    if (!questionSolutionMode && !localShowSolution) {
      return null;
    }

    // Show solution when either:
    // 1. Question solution mode is ON (show immediately), OR
    // 2. User clicked to view solution (localShowSolution is true)
    if (questionSolutionMode || localShowSolution) {
      return (
        <div id="solution-section" className="overflow-hidden relative">
          {currentSolution?.questionId?.videoSolution && (
            <VideoCipherPlayer
              key={currentSolution?.questionId?.videoSolution}
              videoId={currentSolution?.questionId?.videoSolution}
              questionId={currentSolution?.questionId?._id}
              testId={testId}
              testTitle={testDetails?.title}
              subjectTitle={currentSolution?.questionId?.subjectTitle}
              chapterTitle={currentSolution?.questionId?.chapters[0]?.title}
              topicTitle={currentSolution?.questionId?.topics[0]?.title}
              conceptTitle={currentSolution?.questionId?.concepts[0]?.title}
              questionType={currentSolution?.questionId?.type}
              questionLevel={currentSolution?.questionId?.level}
              attemptStatus={currentSolution?.status}
              source="Solutions"
            />
          )}
          <span className="font-medium text-[13px] text-[var(--q3-neutral-light)]">TEXT SOLUTION</span>
          <div className="mt-3 overflow-x-auto image-invert-wrapper">
            <Latex>{currentSolution?.questionId?.solution?.text}</Latex>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className="flex h-screen bg-[var(--q3-surface-dim)]"
      onContextMenu={(e) => {
        e.preventDefault();
        setContextMenu({
          position: { x: e.clientX, y: e.clientY },
        });
      }}
    >
      {/* Torch Guide */}
      {!user?.similarQuestionTourDone && showTorchGuide && (
        <TorchGuide steps={steps} handleTorchGuideCallback={handleJoyrideCallback} stepIndex={stepIndex} />
      )}

      {!user?.questionSolutionModeTourDone && showTorchGuide && showQuestionSolutionModeTour && (
        <TorchGuide
          steps={questionSolutionModeSteps}
          handleTorchGuideCallback={handleQuestionSolutionModeTourCallback}
          stepIndex={questionSolutionModeStepIndex}
        />
      )}

      {!isDrawer && (
        <div className="invisible">
          <DarkModeToggle />
        </div>
      )}
      <div
        className={`flex flex-col flex-1 overflow-hidden transition-all duration-300 ${isSidebarOpen && !isDrawer ? 'mr-[360px]' : 'mr-0'}`}
      >
        {!isDrawer && (
          <div
            className={`fixed top-0 left-0 ${isSidebarOpen ? 'right-[360px]' : 'right-0'} z-20 border-b border-[var(--q3-stroke-normal)]`}
          >
            {/* Action Bar */}
            <div className="pl-4 pr-4 sm:pr-6 py-3 bg-[var(--q3-surface-default)] flex flex-row items-center">
              {/* Back + Title */}
              <div
                className="flex flex-row justify-center items-center cursor-pointer overflow-hidden"
                onClick={() => navigate(`/${packId}/${testId}/analysis`)}
              >
                <ArrowLeftIcon size={24} />
                <div className="ml-2 flex flex-col min-w-0 w-full">
                  <p className="text-xs sm:text-sm">Solutions</p>
                  <p className="text-[var(--q3-neutral-light)] text-xs sm:text-sm truncate">{testDetails?.title}</p>
                </div>
              </div>

              {/* Right-side controls (grouped) */}
              <div className="ml-auto flex flex-row items-center gap-2">
                {/* Settings Dropdown */}
                <div className="relative">
                  <div
                    className="
                      flex justify-center items-center gap-1
                      h-8 min-w-8 min-h-8 max-h-8
                      px-3 py-1
                      rounded-full
                      border border-[var(--q3-stroke-normal)]
                      bg-[var(--q3-surface-default)]
                      cursor-pointer hover:bg-[var(--q3-surface-dim)]
                      transition-colors
                      settings-button-target
                    " // Added target class here
                    onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  >
                    <GearSixIcon size={20} className="text-[var(--q3-neutral-default)]" />
                    <span className="text-sm text-[var(--q3-neutral-default)] hidden md:block">Settings</span>
                  </div>

                  <SettingsDropdown
                    showSettingsDropdown={showSettingsDropdown}
                    setShowSettingsDropdown={setShowSettingsDropdown}
                    handleApplySettings={handleApplySettings}
                    handleClearAll={handleClearAll}
                    tempShowAttemptAnalysis={tempShowAttemptAnalysis}
                    setTempShowAttemptAnalysis={setTempShowAttemptAnalysis}
                    tempQuestionSolutionMode={tempQuestionSolutionMode}
                    setTempQuestionSolutionMode={setTempQuestionSolutionMode}
                  />
                </div>

                {/* Sidebar Toggle */}
                {!isSidebarOpen && (
                  <div
                    className="px-0 sm:px-3 py-1 font-medium text-[var(--q3-neutral-default)] md:border md:rounded-[12px] flex flex-row items-center gap-2 cursor-pointer"
                    onClick={() => {
                      setIsSidebarOpen(true);
                    }}
                  >
                    <p className="md:block hidden text-sm">Show All Qs</p>
                    <TextIndentIcon size={24} className="text-[var(--q3-accent-normal)]" />
                  </div>
                )}
              </div>
            </div>

            {/* Subject Tabs */}
            <div className="w-full hidden md:block overflow-x-auto">
              <TabsContainer color={selectedSubject?.color}>
                {subjectMeta?.map((tab) => (
                  <Tab
                    key={tab._id}
                    isActive={selectedSubject?.title === tab.title}
                    color={selectedSubject?.title === tab.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                    tabColor={tab.color}
                    onClick={() => handleSubjectChange(tab)}
                  >
                    {tab.icon && <img src={tab.icon} alt={tab.title} className="" />}
                    <p className="mb-0">{tab.title}</p>
                  </Tab>
                ))}
              </TabsContainer>
            </div>
          </div>
        )}

        {isSolutionLoading ? (
          <SolutionsLoader />
        ) : (
          <div
            id="scrollable-container"
            className={`flex-1 overflow-x-hidden p-1 pb-[84px] ${!isDrawer && 'pt-[70px] md:pt-[124px]'}`}
          >
            {/* Question Metadata */}
            <div
              ref={questionMetaDataFocusRef}
              className="bg-[var(--q3-surface-default)] mx-2 py-3 pl-6 pr-2 text-[14px] border-r border-l border-t border-[var(--q3-stroke-light)] rounded-t-[7px] flex items-center justify-between min-h-[48px]"
            >
              <div className="flex flex-row items-center gap-x-3 flex-1 min-w-0">
                <div className="text-[var(--q3-neutral-light)] text-sm font-medium whitespace-nowrap">
                  Q{offset + 1}
                </div>
                <div
                  className={`px-2 py-[2px] text-xs rounded whitespace-nowrap ${
                    currentSolution?.marksScored === 0
                      ? 'bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-light)]'
                      : currentSolution?.marksScored < 0
                        ? 'bg-[var(--q3-danger-light)] text-[var(--q3-danger-normal)]'
                        : 'bg-[var(--q3-success-light)] text-[var(--q3-success-normal)]'
                  }`}
                >
                  {currentSolution?.marksScored > 0
                    ? '+' + currentSolution?.marksScored
                    : currentSolution?.marksScored < 0
                      ? currentSolution?.marksScored
                      : currentSolution?.marksScored}
                </div>
                <div className="bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-light)] px-2 py-[2px] flex items-center rounded text-xs whitespace-nowrap">
                  {currentSolution?.questionId?.level === 1
                    ? 'EASY'
                    : currentSolution?.questionId?.level === 2
                      ? 'MODERATE'
                      : 'TOUGH'}
                </div>
              </div>
              <div className="flex flex-row items-center gap-3 md:gap-4 flex-shrink-0 ml-4">
                <div
                  className="cursor-pointer md:border md:border-[var(--q3-stroke-normal)] font-medium text-[var(--q3-accent-normal)] py-1 rounded-[52px] text-sm"
                  onClick={handleBookmarkDrawer}
                >
                  <div className="flex flex-row items-center md:ml-2 md:mr-3">
                    <PencilIcon size={16} />
                    <span className="ml-2 hidden md:block">Notes</span>
                  </div>
                </div>
                {!isLms && (
                  <CustomTooltip text={'Press'} shortcutText={'Shift+B'}>
                    <div
                      className="cursor-pointer md:border md:border-[var(--q3-stroke-normal)] font-medium text-[var(--q3-accent-normal)] py-1 rounded-[52px] text-sm"
                      onClick={handleBookmarkDrawer}
                    >
                      <div className="flex flex-row items-center md:ml-2 md:mr-3">
                        <BookmarkSimpleIcon
                          size={16}
                          weight={`${questionNotebooks?.length > 0 ? 'fill' : 'regular'}`}
                        />
                        <span className="ml-2 hidden md:block">
                          {questionNotebooks?.length > 0 ? 'Bookmarked' : 'Bookmark'}
                        </span>
                      </div>
                    </div>
                  </CustomTooltip>
                )}
                {showReportButton && !packDetails?.isReportingDisabled && (
                  <div
                    className="cursor-pointer font-medium text-[var(--q3-danger-normal)] py-1 text-sm"
                    onClick={(e) => setShowReportModal(true)}
                  >
                    <div className="flex flex-row items-center mr-3">
                      <WarningIcon size={16} />
                      <span className="ml-2 hidden md:block">Report</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div
              id="question-section"
              ref={questionFocusRef}
              className="relative bg-[var(--q3-surface-default)] border-t border-l border-r border-[var(--q3-stroke-light)] mx-2 p-6 overflow-hidden"
            >
              {/* Question Text */}
              <div>
                {questionNotes && (
                  <div className="flex flex-row items-center gap-2 mb-6 bg-[var(--q3-surface-dim)] p-2 rounded-lg">
                    <p className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">My Note:</p>
                    <p className="text-sm text-[var(--q3-neutral-default)] truncate flex-1">{questionNotes}</p>
                    <p
                      className="text-xs text-[var(--q3-accent-normal)] font-medium cursor-pointer whitespace-nowrap"
                      onClick={handleBookmarkDrawer}
                    >
                      View Note
                    </p>
                  </div>
                )}
                <div className="col-12 overflow-x-auto text-lg">
                  {currentSolution?.questionId?.type === 'fillTheBlanks' ? (
                    <p>{getQuestionText(currentSolution, offset)}</p>
                  ) : (
                    <div className="image-invert-wrapper">
                      <Latex>{currentSolution?.questionId?.question?.text}</Latex>
                    </div>
                  )}
                </div>
                {(getEvaluationStatusForEvent() === 'Not Visited' ||
                  getEvaluationStatusForEvent() === 'Not Answered' ||
                  getEvaluationStatusForEvent() === 'Marked for Review') && (
                  <div className="bg-[var(--q3-accent-light)] w-fit mt-6 px-[6px] py-1 rounded text-[var(--q3-neutral-default)] text-[12px] font-semibold">
                    YOU DIDN'T ATTEMPT THIS QUESTION
                  </div>
                )}
                {currentSolution?.questionId?.type === 'numerical' ? (
                  // For numerical questions, display the boxes in full width
                  <div className="w-full pt-6">
                    {getNumericalOption(
                      currentSolution,
                      comparisonData,
                      isDrawer ? true : questionSolutionMode || localShowSolution,
                    )}
                  </div>
                ) : (
                  // For other question types, use the grid layout
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6 items-stretch">
                    {currentSolution?.questionId?.options.map((option, index) => (
                      <div key={index} className="h-full">
                        {getOption(
                          currentSolution,
                          option,
                          index,
                          comparisonData,
                          isDrawer ? true : questionSolutionMode || localShowSolution,
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <SolutionTabs activeTab={solutionTab} onChange={handleSolutionTabChange} />
            <div
              className={`bg-[var(--q3-surface-default)] border-l border-r border-b border-[var(--q3-stroke-light)] mx-2 p-4 md:p-6 overflow-hidden ${
                solutionTab === 'quizrr' ? '' : 'hidden'
              }`}
            >
              <div className="mb-6 flex flex-col gap-4">
                {(() => {
                  const communityBannerVariant = myCommunitySolution?.solution
                    ? 'mine'
                    : communitySolutionsTotal > 0
                      ? 'others'
                      : hasCommunityWriteAccess
                        ? 'none'
                        : null;

                  return (
                    communityBannerVariant && (
                      <CommunitySolutionBanner
                        variant={communityBannerVariant}
                        onClick={() => handleSolutionTabChange('community')}
                      />
                    )
                  );
                })()}
                <div className="flex flex-col text-[var(--q3-neutral-default)] items-start gap-x-2 min-w-0 flex-1">
                  <div className="font-medium text-[13px] text-[var(--q3-neutral-light)]">CHAPTER & TOPIC</div>
                  <div className="w-full flex flex-row">
                    <span className="max-w-[200px]" title={currentSolution?.questionId?.chapters[0]?.title}>
                      {currentSolution?.questionId?.chapters[0]?.title}
                    </span>
                    &nbsp;•&nbsp;
                    <span className="" title={currentSolution?.questionId?.topics[0]?.title}>
                      {currentSolution?.questionId?.topics[0]?.title}
                    </span>
                  </div>
                </div>
                <div ref={solutionRef}>{renderSolutionSection()}</div>
              </div>
            </div>
            {communityTabVisited && (
              <div
                className={`bg-[var(--q3-surface-default)] border-l border-r border-b border-[var(--q3-stroke-light)] mx-2 p-4 md:p-6 overflow-hidden ${
                  solutionTab === 'community' ? '' : 'hidden'
                }`}
              >
                <CommunitySolutionPanel
                  questionId={currentSolution?.questionId?._id}
                  user={user}
                  questionMeta={{
                    packTitle: currentSolution?.questionId?.packTitle,
                    subjectTitle: currentSolution?.questionId?.subjectTitle,
                    chapter: currentSolution?.questionId?.chapters?.[0]?.title,
                    topic: currentSolution?.questionId?.topics?.[0]?.title,
                    testTitle: testDetails?.title,
                    testType: testDetails?.type,
                  }}
                />
              </div>
            )}
            {/* Qs Tags */}
            {!isLms &&
              (getEvaluationStatusForEvent() === 'Not Answered' ||
                getEvaluationStatusForEvent() === 'Marked for Review' ||
                getEvaluationStatusForEvent() === 'Incorrect') && (
                <div
                  className={`bg-[var(--q3-surface-default)] border-l border-r border-b border-[var(--q3-stroke-light)] mx-2 p-6 ${
                    !showAttemptAnalysis || !journeyData || (journeyData?.length === 0 && 'rounded-b-[7px]')
                  }`}
                >
                  <div className="q3-border-gradient-bottom md:q3-border-gradient-right">
                    <div className="q3-border-gradient-inner p-4 flex flex-col md:flex-row justify-between">
                      <div className="flex flex-col gap-0 md:gap-2">
                        <div className="font-semibold text-[var(--q3-blue-normal)] text-lg md:text-2xl">
                          {currentSolution?.status === 'answered' || currentSolution?.status === 'answeredReview'
                            ? 'Why did you get the qs wrong?'
                            : 'Why did you skip this qs?'}
                        </div>
                        <p className="text-[var(--q3-neutral-light)] text-sm md:text-base mb-4 md:mb-0">
                          We'll show you this analysis in my mistakes section
                        </p>

                        {/* Learn More Button with Fancybox */}
                        <Fancybox>
                          <a
                            href={
                              currentSolution?.status === 'answered' || currentSolution?.status === 'answeredReview'
                                ? 'https://www.youtube.com/embed/pW72QZHu888' // Incorrect questions
                                : 'https://www.youtube.com/embed/-RRxpCg906c' // Unattempted questions
                            }
                            data-fancybox
                            data-type="iframe"
                            className="relative block w-fit overflow-hidden"
                          >
                            <LearnMore />
                          </a>
                        </Fancybox>
                      </div>

                      <div className="flex md:flex-col justify-between items-start md:items-end select-none">
                        <QuestionTags
                          questionTags={questionTags}
                          handleUserQuestionTagSelect={handleUserQuestionTagSelect}
                          userQuestionTag={userQuestionTag}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Analytics and Qs Journey */}
            {showAttemptAnalysis && (
              <div className="bg-[var(--q3-surface-default)] border-l border-r border-b rounded-b-[7px] border-[var(--q3-stroke-light)] mx-2 p-4 md:p-6">
                <QuestionAnalytics currentSolution={currentSolution} comparisonData={comparisonData} />
                {journeyData && journeyData?.length > 0 && (
                  <div className="border rounded-[12px] p-4 h-auto select-none relative">
                    <div>
                      <p className="text-[var(--q3-neutral-light)] text-xs font-bold pb-2">QUESTION JOURNEY</p>
                      <div ref={journeyScrollRef} className="overflow-x-auto pb-11">
                        <div className="w-fit cursor-ew-resize">
                          <SingleQuestionJourney journeyData={journeyData} />
                        </div>
                      </div>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center justify-center font-medium px-4 py-1">
                        <CaretCircleLeftIcon
                          size={30}
                          className="cursor-pointer"
                          onClick={() => scrollJourney('left')}
                        />
                        <p className="mx-2 text-[var(--q3-neutral-light)] text-sm whitespace-nowrap">
                          Swipe left right to view full journey
                        </p>
                        <CaretCircleRightIcon
                          size={30}
                          className="cursor-pointer"
                          onClick={() => scrollJourney('right')}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Navigation Section */}
        {/* The button visibility is controlled only by the initial API response */}
        {/* Once shown, it stays shown regardless of attempts */}
        {!isDrawer && showSimilarButton && (
          <div
            className={`fixed bottom-[72px] left-0 right-0 flex justify-center ${isSidebarOpen ? 'mr-[360px]' : 'mr-0'}`}
            style={{ zIndex: 1001 }}
          >
            <div
              className="p-[1px] rounded-[60px] w-fit"
              style={{
                background:
                  'linear-gradient(to right, var(--q3-stroke-light) 0%, var(--q3-stroke-light) 60%, var(--q3-violet-normal) 80%, var(--q3-rose-normal) 100%)',
              }}
            >
              <button
                ref={similarQuestionButtonRef}
                className="similar-question-button-target flex items-center gap-2 px-5 py-2 bg-[var(--q3-surface-default)] rounded-[60px] shadow-md cursor-pointer"
                onClick={handleAttemptSimilarQs}
              >
                <img src={SimilarQuestionSparkle} alt="Similar Question Sparkle" width={22} height={22} />
                <span
                  style={{
                    background: 'linear-gradient(to right, var(--q3-violet-normal), var(--q3-rose-normal))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  Attempt Similar Qs
                </span>
              </button>
            </div>
          </div>
        )}

        {/* ---------- FIXED FOOTER SECTION (only nav buttons) ---------- */}
        {!isDrawer && (
          <div
            className={`fixed bottom-0 left-0 right-0 flex items-center justify-center gap-4 bg-[var(--q3-surface-default)] p-4 border-t border-[var(--q3-stroke-normal)] ${isSidebarOpen ? 'mr-[360px]' : 'mr-0'}`}
            style={{ zIndex: 1000 }}
          >
            <CustomTooltip text={'Press'} shortcutIcon={ArrowLeftIcon}>
              <Button
                variant={'outline'}
                className={`rounded-full w-28 !bg-[var(--q3-surface-default)] !text-[var(--q3-neutral-default)] !border-[var(--q3-stroke-normal)] q3-interaction-scale-up`}
                disabled={offset === 0}
                onClick={() => {
                  if (offset === 0) return;
                  handleQuestionChange(true);
                }}
              >
                Previous
              </Button>
            </CustomTooltip>

            <Button
              className={'group !rounded-full'}
              onClick={() => {
                if (!questionSolutionMode && !localShowSolution) {
                  setLocalShowSolution(true);
                  if (onShowSolution) {
                    onShowSolution(true);
                  }
                } else {
                  scrollQuestion();
                }
              }}
            >
              {!isQuestionInFocus && (questionSolutionMode || localShowSolution) ? 'View Question' : 'View Solution'}
              {(questionSolutionMode || localShowSolution) && (
                <div className="font-medium">
                  {isQuestionInFocus ? (
                    <ArrowDownIcon className="group-hover:translate-y-0.5 transition-all duration-300" />
                  ) : (
                    <ArrowUpIcon className="group-hover:-translate-y-0.5 transition-all duration-300" />
                  )}
                </div>
              )}
            </Button>

            <CustomTooltip text={'Press'} shortcutIcon={ArrowRightIcon}>
              <Button
                variant={'outline'}
                className={`rounded-full w-28 !bg-[var(--q3-surface-default)] !text-[var(--q3-neutral-default)] !border-[var(--q3-stroke-normal)] q3-interaction-scale-up`}
                disabled={offset + 1 === totalQuestions}
                onClick={() => {
                  if (offset === totalQuestions - 1) return;
                  handleQuestionChange(false);
                }}
              >
                Next
              </Button>
            </CustomTooltip>
          </div>
        )}
      </div>

      {!isDrawer && (
        <div
          className={`fixed top-0 right-0 h-full w-full sm:w-[360px] bg-[var(--q3-surface-default)] border-l border-[var(--q3-stroke-normal)] py-4 overflow-y-auto z-50 transform transition-transform duration-300 ease-in-out ${
            isSidebarOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ zIndex: 1000 }}
        >
          <div className="mb-4 px-4 flex flex-row justify-between items-center">
            <div>All {currentSolution?.questionId?.subjectTitle} Questions</div>
            <div className="absolute w-fit md:hidden bottom-8 left-1/2 -translate-x-1/2 flex items-center bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-xl p-2 px-4 justify-center gap-2">
              <p className="whitespace-nowrap">Show Attempt Analysis</p>
              <Switch
                className="cursor-pointer"
                checked={showAttemptAnalysis}
                onCheckedChange={() => setShowAttemptAnalysis(!showAttemptAnalysis)}
              />
            </div>
            <CustomTooltip text={'Press'} shortcutText={'Shift + H'}>
              <div
                className="text-[var(--q3-accent-normal)] text-sm font-medium flex flex-row items-center gap-2 cursor-pointer"
                onClick={() => {
                  setIsSidebarOpen(false);
                }}
              >
                HIDE
                <TextIndentIcon size={24} />
              </div>
            </CustomTooltip>
          </div>
          <div className="flex flex-row gap-4 mt-3 mb-6 px-4 md:hidden overflow-x-auto">
            {subjectMeta?.map((subject) => (
              <Button
                className={`flex flex-row items-center gap-2 rounded-full hover:bg-[var(--q3-surface-dim)] ${selectedSubject?.title === subject.title ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]' : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-light)] border-1 border-[var(--q3-stroke-light)]'}`}
                onClick={() => handleSubjectChange(subject)}
              >
                {subject.icon && selectedSubject?.title !== subject.title && (
                  <img src={subject.icon} alt={subject.title} className="w-5 h-5" />
                )}
                {selectedSubject?.title === subject.title && <CheckIcon weight="bold" />}
                <p className="mb-0 text-xs">{subject.title}</p>
              </Button>
            ))}
          </div>
          <div className="pt-0 -mt-[5px] mb-4">
            <SmallTabsContainer color={selectedSidebarTab?.textColor}>
              {questionData?.map((tab) => (
                <SmallTab
                  key={tab._id}
                  isActive={selectedSidebarTab?.title === tab.title}
                  color={
                    selectedSidebarTab?.title === tab.title ? selectedSidebarTab?.textColor : 'var(--q3-neutral-light)'
                  }
                  tabColor={tab.textColor}
                  onClick={() => handleSidebarTabSwitch(tab)}
                >
                  <div className="flex flex-col justify-center items-center">
                    <p className="m-0 p-0 text-sm">{tab.title}</p>
                    {selectedSubject && (
                      <p className={`bg-[${tab.color}] text-[12px] rounded-full p-1 px-1.5 text-[${tab.textColor}]`}>
                        {tab[selectedSubject?.id.toString()]?.count ?? 0}
                      </p>
                    )}
                  </div>
                </SmallTab>
              ))}
            </SmallTabsContainer>
          </div>
          <div className="w-fit px-4 flex flex-wrap gap-3" style={{ zIndex: 1000 }}>
            <QuestionListing
              handleQuestionChange={handleQuestionChange}
              offset={offset}
              selectedSidebarTab={selectedSidebarTab}
              selectedSubject={selectedSubject}
              questionStatus={questionStatus}
              isDarkTheme={isDarkTheme}
            />
          </div>
        </div>
      )}
      {showSimilarQsModal && (
        <AttemptSimilarQsModal
          loading={similarQsLoading}
          data={similarQsData || similarQsCache[questionId]}
          onClose={() => setShowSimilarQsModal(false)}
          packId={packId}
          testId={testId}
          checkSimilarQuestionsButton={checkSimilarQuestionsButton}
        />
      )}
      {showReportModal && (
        <ReportModal
          show={showReportModal}
          onClose={() => setShowReportModal(false)}
          onReport={reportQuestion}
          isLoading={isLoading}
          isReported={isReported}
          reportOptions={reportOptions}
          handleOnChange={handleOnChange}
          commentsRef={commentsRef}
          reportMessage={reportMessage}
          DAY_LIMIT={DAY_LIMIT}
        />
      )}
      {showBookmarkDrawer && (
        <div style={{ zIndex: 1001, position: 'relative' }}>
          <BookmarkDrawerGlobal
            questionId={questionId}
            packId={packId}
            testId={testId}
            handleShowDrawer={handleBookmarkDrawer}
            handleQuestionNotes={handleQuestionNotes}
            handleQuestionBoomark={handleQuestionBoomark}
          />
        </div>
      )}

      {contextMenu && (
        <ContextMenu
          items={contextMenuItems}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          onItemClick={handleContextMenuAction}
        />
      )}
    </div>
  );
}
